/**
 * Admin Help Sections Page
 *
 * CRUD for the context-aware Help assistant topics (skillsnap-help table).
 * Each topic has steps; a step can point its "Show me" button at a real UI
 * element via a highlight target chosen from HIGHLIGHT_TARGETS.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../../services/api';
import {
  Save, Loader2, AlertCircle, CheckCircle, Plus, Trash2,
  ChevronDown, ChevronRight, X, RefreshCw, ArrowUp, ArrowDown,
  ToggleLeft, ToggleRight, Database, Lightbulb,
} from 'lucide-react';
import { HELP_TARGETS } from '../../help/highlightTargets';
import type { HelpTopic, HelpStep, HelpContextKey, HelpTopicType } from '../../help/types';

const CONTEXT_KEYS: { value: HelpContextKey; label: string }[] = [
  { value: '', label: 'None (browse only)' },
  { value: 'no_resume', label: 'Default when user has 0 resumes' },
  { value: 'no_jobs', label: 'Default when user has 0 jobs' },
  { value: 'pipeline', label: 'Default when user has 1–50 jobs' },
  { value: 'tips_pool', label: 'Default when user has >50 jobs' },
];

const TOPIC_TYPES: { value: HelpTopicType; label: string }[] = [
  { value: 'topic', label: 'Topic (numbered steps)' },
  { value: 'super', label: 'Super (bundles child topics)' },
  { value: 'tip', label: 'Tip pool (one shown at random)' },
];

function blankTopic(): HelpTopic {
  return {
    topic_id: '',
    title: '',
    short_desc: '',
    category: 'General',
    context_key: '',
    topic_type: 'topic',
    children: [],
    steps: [{ text: '' }],
    sort_order: 100,
    enabled: true,
  };
}

const inputCls =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500';

export function HelpSections() {
  const [topics, setTopics] = useState<HelpTopic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<HelpTopic | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const list = await api.adminListHelpTopics();
      setTopics(list);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load help topics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function flashSuccess(msg: string) {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 3000);
  }

  function startEdit(topic: HelpTopic) {
    setExpandedId(topic.topic_id);
    setIsNew(false);
    setDraft(JSON.parse(JSON.stringify(topic)));
  }

  function startNew() {
    setExpandedId('__new__');
    setIsNew(true);
    setDraft(blankTopic());
  }

  function cancelEdit() {
    setExpandedId(null);
    setDraft(null);
    setIsNew(false);
  }

  function patch(p: Partial<HelpTopic>) {
    setDraft((d) => (d ? { ...d, ...p } : d));
  }

  // --- step helpers ---
  function patchStep(i: number, p: Partial<HelpStep>) {
    setDraft((d) => {
      if (!d) return d;
      const steps = d.steps.map((s, idx) => (idx === i ? { ...s, ...p } : s));
      return { ...d, steps };
    });
  }
  function addStep() {
    setDraft((d) => (d ? { ...d, steps: [...d.steps, { text: '' }] } : d));
  }
  function removeStep(i: number) {
    setDraft((d) => (d ? { ...d, steps: d.steps.filter((_, idx) => idx !== i) } : d));
  }
  function moveStep(i: number, dir: -1 | 1) {
    setDraft((d) => {
      if (!d) return d;
      const j = i + dir;
      if (j < 0 || j >= d.steps.length) return d;
      const steps = [...d.steps];
      [steps[i], steps[j]] = [steps[j], steps[i]];
      return { ...d, steps };
    });
  }

  function toggleChild(id: string) {
    setDraft((d) => {
      if (!d) return d;
      const has = d.children.includes(id);
      return { ...d, children: has ? d.children.filter((c) => c !== id) : [...d.children, id] };
    });
  }

  async function handleSave() {
    if (!draft) return;
    if (!draft.title.trim()) { setError('Title is required'); return; }
    setSaving(true);
    setError(null);
    try {
      const idForPath = isNew ? (draft.topic_id.trim() || draft.title) : draft.topic_id;
      const { topic } = await api.adminSaveHelpTopic(idForPath, draft);
      flashSuccess(`Saved "${topic.title}"`);
      await load();
      setExpandedId(topic.topic_id);
      setIsNew(false);
      setDraft(topic);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to save topic');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!draft || isNew) return;
    if (!confirm(`Delete help topic "${draft.title}"? This cannot be undone.`)) return;
    setDeleting(true);
    setError(null);
    try {
      await api.adminDeleteHelpTopic(draft.topic_id);
      flashSuccess(`Deleted "${draft.title}"`);
      cancelEdit();
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to delete topic');
    } finally {
      setDeleting(false);
    }
  }

  async function handleSeed() {
    if (!confirm('Seed default help topics? Existing topics are preserved (only missing ones are added).')) return;
    setSeeding(true);
    setError(null);
    try {
      const res = await api.adminSeedHelpTopics(false);
      flashSuccess(res.message || `Seeded ${res.created}, skipped ${res.skipped}`);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to seed topics');
    } finally {
      setSeeding(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto">
      <div className="p-6 max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Help Docs</h1>
            <p className="text-sm text-gray-500 mt-1">
              {topics.length} topics in <code className="bg-gray-100 px-1 rounded text-xs">skillsnap-help</code>
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2 text-sm font-medium"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <button
              onClick={handleSeed}
              disabled={seeding}
              className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2 text-sm font-medium disabled:opacity-50"
            >
              {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
              Seed Defaults
            </button>
            <button
              onClick={startNew}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2 font-medium"
            >
              <Plus className="w-4 h-4" />
              New Topic
            </button>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="flex items-center p-3 mb-4 bg-red-50 text-red-700 rounded-lg text-sm">
            <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
            {error}
            <button onClick={() => setError(null)} className="ml-auto"><X className="w-4 h-4" /></button>
          </div>
        )}
        {success && (
          <div className="flex items-center p-3 mb-4 bg-green-50 text-green-700 rounded-lg text-sm">
            <CheckCircle className="w-4 h-4 mr-2 flex-shrink-0" />
            {success}
          </div>
        )}

        {/* New topic editor */}
        {isNew && draft && expandedId === '__new__' && (
          <div className="mb-4 bg-white rounded-lg border border-primary-300 shadow-sm">
            <div className="px-4 py-3 border-b border-gray-100 font-semibold text-gray-900">New Topic</div>
            <div className="p-4">
              <TopicEditor
                draft={draft}
                isNew
                allTopics={topics}
                patch={patch}
                patchStep={patchStep}
                addStep={addStep}
                removeStep={removeStep}
                moveStep={moveStep}
                toggleChild={toggleChild}
                onSave={handleSave}
                onCancel={cancelEdit}
                onDelete={handleDelete}
                saving={saving}
                deleting={deleting}
              />
            </div>
          </div>
        )}

        {/* Topic list */}
        <div className="space-y-3">
          {topics.map((topic) => {
            const expanded = expandedId === topic.topic_id;
            return (
              <div key={topic.topic_id} className="bg-white rounded-lg border border-gray-200 shadow-sm">
                <div
                  className="flex items-center px-4 py-3 cursor-pointer hover:bg-gray-50 rounded-t-lg"
                  onClick={() => (expanded ? cancelEdit() : startEdit(topic))}
                >
                  {expanded
                    ? <ChevronDown className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                    : <ChevronRight className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />}
                  {topic.topic_type === 'tip' && <Lightbulb className="w-4 h-4 text-amber-500 mr-1.5 flex-shrink-0" />}
                  <span className="font-semibold text-sm text-gray-900 mr-2">{topic.title}</span>
                  <code className="text-xs text-gray-400 mr-2">{topic.topic_id}</code>
                  {!topic.enabled && (
                    <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full mr-2">disabled</span>
                  )}
                  {topic.context_key && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full mr-2">{topic.context_key}</span>
                  )}
                  <span className="ml-auto text-xs text-gray-400">{topic.category}</span>
                </div>

                {expanded && draft && !isNew && (
                  <div className="px-4 pb-4 border-t border-gray-100">
                    <div className="pt-4">
                      <TopicEditor
                        draft={draft}
                        isNew={false}
                        allTopics={topics}
                        patch={patch}
                        patchStep={patchStep}
                        addStep={addStep}
                        removeStep={removeStep}
                        moveStep={moveStep}
                        toggleChild={toggleChild}
                        onSave={handleSave}
                        onCancel={cancelEdit}
                        onDelete={handleDelete}
                        saving={saving}
                        deleting={deleting}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {topics.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No help topics yet. Click <b>Seed Defaults</b> to load the starter set, or <b>New Topic</b>.
          </div>
        )}
      </div>
    </div>
  );
}

// Searchable picker over the (large) hierarchical target catalog: type to filter
// by key or label, then choose. Shows the dotted key + human label per option.
function TargetPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return HELP_TARGETS;
    return HELP_TARGETS.filter((t) => t.key.toLowerCase().includes(s) || t.label.toLowerCase().includes(s));
  }, [q]);
  return (
    <div className="space-y-1">
      <input
        className={`${inputCls} bg-white`}
        placeholder="Filter targets… (e.g. addjob, resume, ready)"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      <select
        className={`${inputCls} bg-white font-mono text-xs`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">— none —</option>
        {value && !filtered.some((t) => t.key === value) && <option value={value}>{value}</option>}
        {filtered.slice(0, 300).map((t) => (
          <option key={t.key} value={t.key}>{t.key} — {t.label}</option>
        ))}
      </select>
      {q && <p className="text-[11px] text-gray-400">{filtered.length} match{filtered.length === 1 ? '' : 'es'}</p>}
    </div>
  );
}

interface EditorProps {
  draft: HelpTopic;
  isNew: boolean;
  allTopics: HelpTopic[];
  patch: (p: Partial<HelpTopic>) => void;
  patchStep: (i: number, p: Partial<HelpStep>) => void;
  addStep: () => void;
  removeStep: (i: number) => void;
  moveStep: (i: number, dir: -1 | 1) => void;
  toggleChild: (id: string) => void;
  onSave: () => void;
  onCancel: () => void;
  onDelete: () => void;
  saving: boolean;
  deleting: boolean;
}

function TopicEditor(props: EditorProps) {
  const { draft, isNew, allTopics, patch, patchStep, addStep, removeStep, moveStep, toggleChild } = props;
  const showSteps = draft.topic_type !== 'super';
  const showChildren = draft.topic_type === 'super';

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Title</label>
          <input className={inputCls} value={draft.title} onChange={(e) => patch({ title: e.target.value })} />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Topic ID {isNew ? '(optional — derived from title)' : '(read-only)'}
          </label>
          <input
            className={`${inputCls} font-mono ${isNew ? '' : 'bg-gray-100 text-gray-500'}`}
            value={draft.topic_id}
            readOnly={!isNew}
            onChange={(e) => patch({ topic_id: e.target.value })}
            placeholder="auto"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
          <input className={inputCls} value={draft.category} onChange={(e) => patch({ category: e.target.value })} />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Sort order</label>
          <input
            type="number"
            className={inputCls}
            value={draft.sort_order}
            onChange={(e) => patch({ sort_order: parseInt(e.target.value || '0', 10) })}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
          <select className={inputCls} value={draft.topic_type} onChange={(e) => patch({ topic_type: e.target.value as HelpTopicType })}>
            {TOPIC_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Context (default trigger)</label>
          <select className={inputCls} value={draft.context_key} onChange={(e) => patch({ context_key: e.target.value as HelpContextKey })}>
            {CONTEXT_KEYS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Short description (the problem this solves)
        </label>
        <textarea
          className={`${inputCls} resize-y`}
          rows={2}
          value={draft.short_desc}
          onChange={(e) => patch({ short_desc: e.target.value })}
        />
      </div>

      <label className="flex items-center gap-2 cursor-pointer">
        <button type="button" onClick={() => patch({ enabled: !draft.enabled })}>
          {draft.enabled ? <ToggleRight className="w-9 h-9 text-green-600" /> : <ToggleLeft className="w-9 h-9 text-gray-400" />}
        </button>
        <span className="text-sm text-gray-700">{draft.enabled ? 'Enabled (visible to users)' : 'Disabled (hidden)'}</span>
      </label>

      {/* Children (super topics) */}
      {showChildren && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Child topics (shown in order checked)
          </label>
          <div className="border border-gray-200 rounded-lg p-2 max-h-48 overflow-y-auto space-y-1">
            {allTopics.filter((t) => t.topic_id !== draft.topic_id).map((t) => {
              const idx = draft.children.indexOf(t.topic_id);
              const checked = idx >= 0;
              return (
                <label key={t.topic_id} className="flex items-center gap-2 text-sm text-gray-700 px-1 py-0.5">
                  <input type="checkbox" checked={checked} onChange={() => toggleChild(t.topic_id)} />
                  {checked && <span className="text-xs text-primary-600 font-semibold w-4">{idx + 1}</span>}
                  <span>{t.title}</span>
                  <code className="text-xs text-gray-400">{t.topic_id}</code>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* Steps */}
      {showSteps && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-xs font-medium text-gray-600">
              {draft.topic_type === 'tip' ? 'Tips (one shown at random)' : 'Steps'}
            </label>
            <button onClick={addStep} className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1">
              <Plus className="w-3.5 h-3.5" /> Add {draft.topic_type === 'tip' ? 'tip' : 'step'}
            </button>
          </div>
          <p className="text-xs text-gray-400 mb-2">
            Text supports inline HTML: <code>&lt;b&gt;</code>, <code>&lt;code&gt;</code>, and links
            (<code>&lt;a href="…" target="_blank"&gt;</code>).
          </p>
          <div className="space-y-3">
            {draft.steps.map((step, i) => (
              <div key={i} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                <div className="flex items-start gap-2">
                  <span className="mt-2 text-xs font-semibold text-gray-400 w-5 text-right">{i + 1}</span>
                  <div className="flex-1 space-y-2">
                    <textarea
                      className={`${inputCls} resize-y bg-white`}
                      rows={2}
                      value={step.text}
                      onChange={(e) => patchStep(i, { text: e.target.value })}
                      placeholder="Step text…"
                    />
                    <div className="space-y-2">
                      <div>
                        <label className="block text-[11px] text-gray-500 mb-0.5">Highlight target (optional)</label>
                        <TargetPicker
                          value={step.highlight_target || ''}
                          onChange={(v) => patchStep(i, { highlight_target: v || undefined })}
                        />
                      </div>
                      {step.highlight_target && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          <div>
                            <label className="block text-[11px] text-gray-500 mb-0.5">Button label</label>
                            <input
                              className={`${inputCls} bg-white`}
                              value={step.button_label || ''}
                              onChange={(e) => patchStep(i, { button_label: e.target.value })}
                              placeholder="Show me"
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] text-gray-500 mb-0.5">Disabled hover text (optional)</label>
                            <input
                              className={`${inputCls} bg-white`}
                              value={step.disabled_hint || ''}
                              onChange={(e) => patchStep(i, { disabled_hint: e.target.value || undefined })}
                              placeholder="overrides default e.g. “Add a job to see this”"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-[11px] text-gray-500 mb-0.5">
                              Open first (optional) — comma-separated target keys clicked before highlighting
                            </label>
                            <input
                              className={`${inputCls} bg-white font-mono text-xs`}
                              value={(step.open_first || []).join(', ')}
                              onChange={(e) => patchStep(i, { open_first: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
                              placeholder="mainapp.addjob, mainapp.addjob.fromurl"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <button onClick={() => moveStep(i, -1)} disabled={i === 0} className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
                    <button onClick={() => moveStep(i, 1)} disabled={i === draft.steps.length - 1} className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
                    <button onClick={() => removeStep(i)} className="p-1 text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <button
            onClick={props.onSave}
            disabled={props.saving}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-1.5 text-sm font-medium"
          >
            {props.saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {props.saving ? 'Saving…' : 'Save'}
          </button>
          <button onClick={props.onCancel} className="px-3 py-2 text-gray-500 hover:text-gray-700 text-sm">Cancel</button>
        </div>
        {!isNew && (
          <button
            onClick={props.onDelete}
            disabled={props.deleting}
            className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-1.5 text-sm"
          >
            {props.deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            Delete
          </button>
        )}
      </div>
    </div>
  );
}
